import clsx from 'clsx'
import * as React from 'react'
import { Logger } from 'src/3rds/cube'
import { classToFComponent, FCClassContext } from 'src/3rds/cube-react'

import { makeStyles } from 'tss-react/mui'

import Autocomplete from '@mui/material/Autocomplete'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import DownloadIcon from '@mui/icons-material/Download'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import RestartAltIcon from '@mui/icons-material/RestartAlt'

import { CadastroLoteScope, LoteItem, Parceiro } from '../CadastroLote.scopes'
import { conferirCnpjs, formatCNPJ } from '../CadastroLote.service'

const LOG = Logger.get('CadastroLote-VIEW')

// Rótulos legíveis para os status devolvidos pela LIA.
const ROTULO_STATUS: Record<string, string> = {
    pendente: 'Na fila',
    cadastrado: 'Cadastrado',
    ja_registrado_para_o_parceiro: 'Já é do parceiro',
    ja_e_cliente: 'Grupo já é cliente',
    reservado_para_outro_parceiro: 'Reservado p/ outro parceiro',
    etapa_avancada_do_funil: 'Etapa avançada do funil',
    grupo_com_menos_de_50_funcionarios: 'Menos de 50 funcionários',
    simples_nacional: 'Optante do Simples',
    orgao_publico: 'Órgão público',
    empresa_inativa: 'Empresa inativa',
    cnpj_nao_localizado: 'CNPJ não localizado',
    cnpj_invalido: 'CNPJ inválido',
    recusado: 'Recusado',
    erro: 'Erro'
}

const COR_STATUS: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
    pendente: 'default',
    cadastrado: 'success',
    erro: 'error',
    cnpj_invalido: 'error'
}

const ROTULO_LOTE: Record<string, string> = {
    pendente: 'Na fila',
    processando: 'Processando',
    concluido: 'Concluído',
    cancelado: 'Cancelado',
    erro: 'Erro'
}

const rotuloStatus = (status: string) => ROTULO_STATUS[status] ?? status
const corStatus = (status: string) => COR_STATUS[status] ?? 'info'
const formatarDataHora = (data?: Date) => (data ? data.toLocaleString('pt-BR') : '—')

const useStyles = makeStyles()((theme) => ({
    view: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        padding: theme.spacing(3),
        rowGap: theme.spacing(3)
    },
    card: {
        borderRadius: 14,
        boxShadow: '0px 2px 12px rgba(0,0,0,0.05)'
    },
    formRow: {
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        marginTop: theme.spacing(2)
    },
    parceiroField: {
        minWidth: 380,
        flexGrow: 1
    },
    acoes: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        flexWrap: 'wrap'
    },
    resumoChips: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginTop: theme.spacing(2)
    },
    progressoLinha: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    progressoBarra: {
        flexGrow: 1,
        height: 10,
        borderRadius: 5
    },
    tabela: {
        marginTop: theme.spacing(2),
        maxHeight: 480
    },
    cnpjCell: {
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums'
    },
    mensagemCell: {
        maxWidth: 460,
        color: theme.palette.text.secondary
    },
    historicoLinha: {
        cursor: 'pointer'
    }
}))

export type CadastroLoteViewProps = {
    className?: string
    scope: CadastroLoteScope
}

class CadastroLoteClass implements FCClassContext<CadastroLoteViewProps> {
    // :: Fields

    scope!: CadastroLoteScope
    styles!: ReturnType<typeof useStyles>

    // :: emissors

    readonly emitSelecionarParceiro = (_evt: unknown, parceiro: Parceiro | null) =>
        this.scope.onSelecionarParceiro(parceiro ?? undefined).catch(LOG.caught)

    readonly emitBuscarParceiro = (_evt: unknown, texto: string, motivo: string) => {
        // 'reset' é disparado pelo próprio MUI ao selecionar/limpar; só interessa o que o usuário digita.
        if (motivo === 'input') {
            this.scope.onBuscarParceiro(texto).catch(LOG.caught)
        }
    }

    readonly emitTextoCnpjsChange = (evt: React.ChangeEvent<HTMLInputElement>) =>
        this.scope.onTextoCnpjsChange(evt.target.value).catch(LOG.caught)

    readonly emitConferir = () => this.scope.onConferir().catch(LOG.caught)
    readonly emitFecharConfirmacao = () => this.scope.onFecharConfirmacao().catch(LOG.caught)
    readonly emitConfirmarDisparo = () => this.scope.onConfirmarDisparo().catch(LOG.caught)
    readonly emitCancelarLote = () => this.scope.onCancelarLote().catch(LOG.caught)
    readonly emitBaixarCsv = () => this.scope.onBaixarCsv().catch(LOG.caught)
    readonly emitNovoLote = () => this.scope.onNovoLote().catch(LOG.caught)
    readonly emitAbrirLote = (loteId: string) => () => this.scope.onAbrirLote(loteId).catch(LOG.caught)

    // :: Methods

    onSyncState({ scope }: CadastroLoteViewProps) {
        this.scope = scope
        this.styles = useStyles()
    }

    render({ className }: CadastroLoteViewProps) {
        const { classes } = this.styles

        return (
            <div className={clsx(classes.view, className)}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Cadastro de CNPJs em lote
                    </Typography>
                    <Typography color="text.secondary" fontSize={15}>
                        Registra os CNPJs no HubSpot em nome de um parceiro, aplicando as mesmas regras da LIA. Empresa
                        já cliente ou reservada para outro parceiro não é cadastrada.
                    </Typography>
                </Box>

                {this.renderFormulario()}
                {this.renderAndamento()}
                {this.renderHistorico()}
                {this.renderConfirmacao()}
            </div>
        )
    }

    renderFormulario() {
        const { scope } = this
        const { classes } = this.styles

        // Contagem ao vivo enquanto o operador cola a lista.
        const previaAoVivo = conferirCnpjs(scope.textoCnpjs)

        return (
            <Card className={classes.card}>
                <CardContent>
                    <Typography variant="h6" fontWeight={600}>
                        Novo lote
                    </Typography>

                    <Box className={classes.formRow}>
                        <Autocomplete
                            className={classes.parceiroField}
                            options={scope.parceiros}
                            value={scope.parceiroSelecionado ?? null}
                            onChange={this.emitSelecionarParceiro}
                            onInputChange={this.emitBuscarParceiro}
                            // A lista já vem filtrada pelo servidor (são milhares de parceiros):
                            // filtrar de novo no cliente esconderia resultados válidos.
                            filterOptions={(opcoes) => opcoes}
                            loading={scope.carregandoParceiros}
                            loadingText="Buscando parceiros…"
                            noOptionsText="Nenhum parceiro encontrado"
                            getOptionLabel={(parceiro) => `${parceiro.nome} — ${parceiro.email}`}
                            isOptionEqualToValue={(a, b) => a.ownerId === b.ownerId}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Parceiro dono das negociações"
                                    size="small"
                                    placeholder="Digite o nome ou e-mail do parceiro"
                                />
                            )}
                        />
                    </Box>

                    <Box mt={2}>
                        <TextField
                            label="CNPJs (um por linha)"
                            multiline
                            minRows={8}
                            maxRows={18}
                            fullWidth
                            value={scope.textoCnpjs}
                            onChange={this.emitTextoCnpjsChange}
                            placeholder={'62959855000100\n11083023000123\n...'}
                            helperText={
                                scope.textoCnpjs
                                    ? `${previaAoVivo.validos.length} válidos · ` +
                                      `${previaAoVivo.duplicados.length} duplicados · ` +
                                      `${previaAoVivo.invalidos.length} inválidos`
                                    : 'Aceita CNPJ com ou sem pontuação, separado por quebra de linha, vírgula ou espaço.'
                            }
                        />
                    </Box>

                    {scope.erroFormulario && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {scope.erroFormulario}
                        </Alert>
                    )}

                    <Box className={classes.acoes}>
                        <Button
                            variant="contained"
                            startIcon={<PlayArrowIcon />}
                            onClick={this.emitConferir}
                            disabled={scope.enviando}
                        >
                            Conferir e iniciar
                        </Button>
                        {scope.lote && (
                            <Button variant="text" startIcon={<RestartAltIcon />} onClick={this.emitNovoLote}>
                                Limpar
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        )
    }

    renderAndamento() {
        const { scope } = this
        const { classes } = this.styles
        const lote = scope.lote

        if (!lote) {
            return null
        }

        const emAndamento = lote.status === 'pendente' || lote.status === 'processando'
        const progresso = lote.total > 0 ? Math.round((lote.processados / lote.total) * 100) : 0

        return (
            <Card className={classes.card}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Typography variant="h6" fontWeight={600}>
                            Lote {lote.id} — {lote.ownerNome}
                        </Typography>
                        <Chip
                            size="small"
                            label={ROTULO_LOTE[lote.status] ?? lote.status}
                            color={lote.status === 'concluido' ? 'success' : emAndamento ? 'info' : 'default'}
                        />
                        {emAndamento && <CircularProgress size={18} />}
                    </Box>

                    <Typography color="text.secondary" fontSize={14} mt={0.5}>
                        Iniciado em {formatarDataHora(lote.startedAt ?? lote.createdAt)}
                        {lote.finishedAt ? ` · finalizado em ${formatarDataHora(lote.finishedAt)}` : ''}
                        {lote.solicitante ? ` · por ${lote.solicitante}` : ''}
                    </Typography>

                    {lote.erro && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {lote.erro}
                        </Alert>
                    )}

                    <Box className={classes.progressoLinha}>
                        <LinearProgress className={classes.progressoBarra} variant="determinate" value={progresso} />
                        <Typography fontSize={14} whiteSpace="nowrap">
                            {lote.processados} de {lote.total} · {lote.cadastrados} cadastrados
                        </Typography>
                    </Box>

                    <Box className={classes.resumoChips}>
                        {lote.resumo.map((item) => (
                            <Chip
                                key={item.status}
                                size="small"
                                variant="outlined"
                                color={corStatus(item.status)}
                                label={`${rotuloStatus(item.status)}: ${item.qtde}`}
                            />
                        ))}
                    </Box>

                    <Box className={classes.acoes}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={this.emitBaixarCsv}
                            disabled={scope.baixandoCsv}
                        >
                            Baixar CSV
                        </Button>
                        {emAndamento && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<StopIcon />}
                                onClick={this.emitCancelarLote}
                            >
                                Cancelar lote
                            </Button>
                        )}
                    </Box>

                    <Divider sx={{ mt: 3 }} />

                    {this.renderTabelaItens(lote.itens)}
                </CardContent>
            </Card>
        )
    }

    renderTabelaItens(itens: LoteItem[]) {
        const { classes } = this.styles

        return (
            <TableContainer className={classes.tabela}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell width={60}>#</TableCell>
                            <TableCell>CNPJ</TableCell>
                            <TableCell>Razão social</TableCell>
                            <TableCell>Situação</TableCell>
                            <TableCell>Motivo</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {itens.map((item) => (
                            <TableRow key={item.seq} hover>
                                <TableCell>{item.seq}</TableCell>
                                <TableCell className={classes.cnpjCell}>{formatCNPJ(item.cnpj)}</TableCell>
                                <TableCell>{item.razaoSocial ?? '—'}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        color={corStatus(item.status)}
                                        variant={item.status === 'cadastrado' ? 'filled' : 'outlined'}
                                        label={rotuloStatus(item.status)}
                                    />
                                </TableCell>
                                <TableCell className={classes.mensagemCell}>
                                    <Tooltip title={item.mensagem ?? ''}>
                                        <span>{item.mensagem ?? '—'}</span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    renderHistorico() {
        const { scope } = this
        const { classes } = this.styles

        if (scope.historico.length === 0) {
            return null
        }

        return (
            <Card className={classes.card}>
                <CardContent>
                    <Typography variant="h6" fontWeight={600}>
                        Lotes anteriores
                    </Typography>

                    <TableContainer className={classes.tabela}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width={80}>Lote</TableCell>
                                    <TableCell>Parceiro</TableCell>
                                    <TableCell>Início</TableCell>
                                    <TableCell align="right">CNPJs</TableCell>
                                    <TableCell align="right">Cadastrados</TableCell>
                                    <TableCell>Situação</TableCell>
                                    <TableCell width={80} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.historico.map((lote) => (
                                    <TableRow
                                        key={lote.id}
                                        hover
                                        className={classes.historicoLinha}
                                        onClick={this.emitAbrirLote(lote.id)}
                                        selected={lote.id === scope.lote?.id}
                                    >
                                        <TableCell>{lote.id}</TableCell>
                                        <TableCell>{lote.ownerNome}</TableCell>
                                        <TableCell>{formatarDataHora(lote.startedAt ?? lote.createdAt)}</TableCell>
                                        <TableCell align="right">{lote.total}</TableCell>
                                        <TableCell align="right">{lote.cadastrados}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={ROTULO_LOTE[lote.status] ?? lote.status}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Link component="button" underline="hover" fontSize={14}>
                                                abrir
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        )
    }

    renderConfirmacao() {
        const { scope } = this
        const previa = scope.previa
        const parceiro = scope.parceiroSelecionado

        return (
            <Dialog open={scope.confirmacaoAberta} onClose={this.emitFecharConfirmacao} maxWidth="sm" fullWidth>
                <DialogTitle>Confirmar o cadastro em lote</DialogTitle>
                <DialogContent>
                    <Typography>
                        Serão consultados <b>{previa?.validos.length ?? 0} CNPJs</b> em nome de <b>{parceiro?.nome}</b>{' '}
                        ({parceiro?.email}).
                    </Typography>

                    {(previa?.duplicados.length ?? 0) > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {previa?.duplicados.length} CNPJ(s) repetido(s) serão processados uma única vez.
                        </Alert>
                    )}

                    {(previa?.invalidos.length ?? 0) > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {previa?.invalidos.length} entrada(s) inválida(s) serão ignoradas:{' '}
                            {previa?.invalidos.slice(0, 10).join(', ')}
                            {(previa?.invalidos.length ?? 0) > 10 ? '…' : ''}
                        </Alert>
                    )}

                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Os negócios aprovados são criados no HubSpot de produção e não são desfeitos automaticamente.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.emitFecharConfirmacao} disabled={scope.enviando}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={this.emitConfirmarDisparo} disabled={scope.enviando}>
                        Iniciar cadastro
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }
}

export const CadastroLoteView = classToFComponent(CadastroLoteClass, React)
export default CadastroLoteView
